import { User } from 'models/User';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyGetUnitQuery } from 'services/UnitsService';
import { useLazyGetUserDetailsQuery, useLazySignInQuery } from 'services/UsersService';
import { localStorageService } from 'services/localStorageService';

export interface AuthContextProps {
  isAuthenticated: boolean;
  isLoading: boolean;
  user?: User;
  token?: string;
  refreshToken?: string;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
}

const signIn = async () => {
  console.log('empty sign in');
};
const signOut = () => {
  console.log('logout');
};

const defaultAuthContext: AuthContextProps = {
  isAuthenticated: false,
  isLoading: false,
  signIn,
  signOut,
};

export const AuthContext = React.createContext<AuthContextProps>(defaultAuthContext);

export interface SessionProviderProps {
  children?: ReactNode;
}

export const SessionProvider = ({ children }: SessionProviderProps) => {
  const localStorageTokens = localStorageService.getTokens();
  const searchParams = new URLSearchParams(location.hash.replace('#', ''));
  const accessToken = searchParams.get('token'),
    paramRefreshToken = searchParams.get('refreshToken'),
    paramUnitId = searchParams.get('unitId');

  const [user, setUser] = useState<User>();
  const [token, setToken] = useState<string | undefined>(
    accessToken ?? localStorageTokens.accessToken,
  );
  const [refreshToken, setRefreshToken] = useState<string | undefined>(
    paramRefreshToken ?? localStorageTokens.refreshToken,
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [signIn] = useLazySignInQuery();
  const [getUserDetails] = useLazyGetUserDetailsQuery();
  const [getUnit] = useLazyGetUnitQuery();

  const loadUser = useCallback(async (): Promise<User> => {
    let userDetails = localStorageService.getUserDetails();
    if (userDetails) return userDetails;

    userDetails = await getUserDetails().unwrap();
    localStorageService.setUserDetails(userDetails);

    return userDetails;
  }, [getUserDetails]);

  useEffect(() => {
    if (accessToken && paramRefreshToken && paramUnitId) {
      localStorageService.setTokens(accessToken, paramRefreshToken);
      localStorageService.setUnitId(paramUnitId);
    }
  }, [accessToken, paramRefreshToken, paramUnitId]);

  useEffect(() => {
    (async () => {
      try {
        if (token && !user) {
          setIsLoading(true);
          await getUnit({ unitId: (paramUnitId ?? localStorageService.getUnitId()) || '' });
          const userDetails = await loadUser();
          setUser(userDetails);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [getUnit, getUserDetails, loadUser, paramUnitId, token, user]);

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        setIsLoading(true);
        const response = await signIn({ email, password }, false).unwrap();
        setToken(response.token);
        setRefreshToken(response.refreshToken);
        localStorageService.setTokens(response.token, response.refreshToken);

        const user = await getUserDetails().unwrap();
        setUser(user);
        localStorageService.setUser(user.email);
        localStorageService.setUnitId(user.unitId);
        localStorageService.setUserDetails(user);
      } finally {
        setIsLoading(false);
      }
    },
    [getUserDetails, signIn],
  );

  const logout = useCallback(async () => {
    setToken(undefined);
    setRefreshToken(undefined);
    localStorageService.clearAll();
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      signIn: login,
      signOut: logout,
      token,
      refreshToken,
    }),
    [isLoading, login, logout, refreshToken, token, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
